import { isCompanyAdmin, isWorkspaceAdmin } from '@/features/userInfo/hooks/useUserInfo';
import { useWorkspace } from '@/features/workspaces/hooks';
import { IconButton } from '@/lib';
import { PlusIcon } from '@/lib/Icons/Icons';
import { useGetWorkspaceInvitesQuery } from '@/services/endpoints/workspaces';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NewWorkspaceInviteRow } from './NewWorkspaceInviteRow';
import { WorkspaceInvitesTable } from './WorkspaceInvitesTable';
import { WorkspaceUsersTable, WorkspaceUsersTableSearch } from './WorkspaceUsersTable';

export const WorkspaceUsers: FC = () => {
  const workspace = useWorkspace();
  const { t } = useTranslation('workspaces');
  const { t: tSettings } = useTranslation('settings');

  const [showNewInviteRow, setShowNewInviteRow] = useState<boolean>(false);

  const { data: invitesData } = useGetWorkspaceInvitesQuery({
    workspaceId: workspace.id,
  });

  const isAdmin = isCompanyAdmin(workspace.companyId) || isWorkspaceAdmin(workspace.id);

  // Check if there are any non-completed invites
  const hasActiveInvites = useMemo(() => {
    if (!invitesData?.items) return false;
    return invitesData.items.filter((invite) => invite.status !== 'COMPLETE').length > 0;
  }, [invitesData?.items]);

  return (
    <div className='flex flex-col gap-6'>
      <div className='flex flex-col gap-4'>
        <div className='flex items-center justify-between'>
          <h3 className='mt-4 text-2xl font-semibold text-gray-900'>{tSettings('workspaceUsers.title')}</h3>
          <WorkspaceUsersTableSearch />
        </div>

        <WorkspaceUsersTable workspaceId={workspace.id} companyId={workspace.companyId} />
      </div>

      {hasActiveInvites && (
        <WorkspaceInvitesTable workspaceId={workspace.id} companyId={workspace.companyId}>
        </WorkspaceInvitesTable>
      )}

      {isAdmin && !showNewInviteRow && (
        <div className='flex justify-start rounded-lg border bg-primary-background p-6 shadow-xs'>
          <IconButton intent='secondary' onClick={() => setShowNewInviteRow(true)} leadingIcon={<PlusIcon />} text={t('users.addNewUser')} />
        </div>
      )}
      {showNewInviteRow && <NewWorkspaceInviteRow onComplete={() => setShowNewInviteRow(false)} workspace={workspace} />}
    </div>
  );
};
